import React, { ChangeEvent, useState } from 'react';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { StyledTableContent, StyledTableRow } from 'shared/ui/Table/ui/StyledTableContent';
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions';
import { ColumnConfigType } from 'shared/types';

type DataTableFrontendPaginationType<DataType> = {
  title: string;
  search?: boolean;
  searchPlaceholder?: string;
  onSearch?: (search: string) => void;
  data: DataType[];
  columnConfig: ColumnConfigType<DataType>[];
  deleteIcon?: boolean;
  handleDrawerDelete?: (id: number, variant: string, title: string) => void;
  variantDrawer?: string;
  titleDrawer?: string;
  emptyListTitle?: string;
  onRowClick?: (id: number | string) => void;
};

export const DataTableFrontendPagination = <DataType,>({
  title,
  search,
  searchPlaceholder = 'Поиск',
  onSearch,
  data,
  columnConfig,
  deleteIcon,
  handleDrawerDelete,
  onRowClick,
  variantDrawer = '',
  titleDrawer = '',
  emptyListTitle = 'Нет данных для отображения',
}: DataTableFrontendPaginationType<DataType>) => {
  const [searchText, setSearchText] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [pagination, setPagination] = useState({
    rowsPerPage: 5,
    page: 0,
  });

  const handleChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    onSearch?.(event.target.value!);
  };

  const handleChangePageParticipants = (event: any, newPage: number) => {
    setPagination({ ...pagination, page: newPage });
  };

  const handleChangeRowsPerPageParticipants = (event: any) =>
    setPagination({
      ...pagination,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
    });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {search ? (
          <Box>
            {showSearch ? (
              <Box>
                <SearchOutlinedIcon />
                <TextField
                  autoFocus
                  variant="standard"
                  value={searchText}
                  size="small"
                  sx={{ width: '425px', pl: '5px', pr: '10px' }}
                  onChange={handleChangeSearch}
                  placeholder={searchPlaceholder}
                />
                <IconButton onClick={() => setShowSearch(false)}>
                  <CloseOutlinedIcon />
                </IconButton>
              </Box>
            ) : (
              <Box
                display="flex"
                alignItems="center"
              >
                <IconButton onClick={() => setShowSearch(true)}>
                  <SearchOutlinedIcon />
                </IconButton>
                <Typography marginLeft="10px">{title}</Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Typography>{title}</Typography>
        )}
      </div>

      {data && data.length > 0 ? (
        <StyledTableContent>
          <Box
            style={{
              marginTop: '12px',
            }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {columnConfig.map((column, index) => (
                      <TableCell
                        key={index}
                        style={{ width: column.width }}
                      >
                        {column.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(pagination.rowsPerPage > 0
                    ? data.slice(
                        pagination.page * pagination.rowsPerPage,
                        pagination.page * pagination.rowsPerPage + pagination.rowsPerPage,
                      )
                    : data
                  ).map((data: any, idx: number) => (
                    <StyledTableRow
                      hover
                      onClick={() => onRowClick && onRowClick(data.id)}
                      key={idx}
                    >
                      {columnConfig.map((column, idx) => (
                        <TableCell
                          key={idx}
                          style={{ width: column.width }}
                        >
                          {data[column.dataFieldName]}
                        </TableCell>
                      ))}
                      {deleteIcon && (
                        <TableCell>
                          <IconButton onClick={() => handleDrawerDelete?.(data.id, variantDrawer, titleDrawer)}>
                            <CloseOutlinedIcon />
                          </IconButton>
                        </TableCell>
                      )}
                    </StyledTableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                      count={data.length}
                      rowsPerPage={pagination.rowsPerPage}
                      page={pagination.page}
                      SelectProps={{
                        inputProps: {
                          'aria-label': 'Строк на странице',
                        },
                      }}
                      onPageChange={handleChangePageParticipants}
                      onRowsPerPageChange={handleChangeRowsPerPageParticipants}
                      ActionsComponent={TablePaginationActions}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Box>
        </StyledTableContent>
      ) : (
        <Box marginTop="30px">
          <Typography>{emptyListTitle}</Typography>
        </Box>
      )}
    </div>
  );
};
